import React, { useState, useEffect, useRef } from 'react';
import './AIReadinessAssessment.css';
import './EmailToast.css';
import NeoChatbot from './NeoChatbot';
import apiService from '../services/api';

const AIReadinessAssessment = ({ user = null }) => {
  const [state, setState] = useState({});
  const [activeDim, setActiveDim] = useState('data');
  const [showDimensionsDropdown, setShowDimensionsDropdown] = useState(false);
  const [showNeoChat, setShowNeoChat] = useState(false);
  const [tabs, setTabs] = useState([]); // Store fetched tabs for potential future use
  const [showMoreDetailsPanel, setShowMoreDetailsPanel] = useState(false);
  const [moreDetailsData, setMoreDetailsData] = useState({
    companyName: '',
    domain: '',
    organization: ''
  });
  const moreDetailsPanelRef = useRef(null);
  const moreDetailsButtonRef = useRef(null);
  
  // Get user data from props, localStorage, or null
  const getUserData = () => {
    if (user) return user;
    const storedUser = localStorage.getItem('aiReadinessUser');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
    return null;
  };

  // Get user email from user data
  const getUserEmail = () => {
    const userData = getUserData();
    const email = userData?.email || null;
    // Normalize email (lowercase and trim) to match backend expectations
    if (email) {
      return email.toLowerCase().trim();
    }
    return null;
  };

  // Fetch AI Readiness tabs from API when component mounts
  useEffect(() => {
    const fetchTabs = async () => {
      try {
        const tabsResponse = await apiService.getTabs('AIReadiness');
        setTabs(tabsResponse.tabs || []);
        console.log('AI Readiness tabs fetched:', tabsResponse.tabs);
      } catch (error) {
        console.error('Error fetching AI Readiness tabs:', error);
        // Continue with hardcoded dimensions if API fails
      }
    };
    fetchTabs();
  }, []);

  const dimensions = [
    {
      id: 'data',
      title: 'Data Maturity & Accessibility',
      color: '#0ea5e9',
      desc: 'Quality, availability, governance, and integration of enterprise data for AI use.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Siloed & Ad-hoc',
          bullets: [
            'Data fragmented across systems',
            'Manual access and low quality',
            'No catalog or governance',
            'Limited analytics usage'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Integrated but Manual',
          bullets: [
            'Partial integration across domains',
            'Basic governance and controls',
            'Manual preparation for analytics',
            'Limited reuse for AI'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Governed & Reusable',
          bullets: [
            'Centralized platforms and catalogs',
            'Automated pipelines',
            'Good data quality metrics',
            'Data reusable for ML/AI'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'AI-Ready Data Fabric',
          bullets: [
            'Semantic layer and data products',
            'Real-time governance',
            'High trust and discoverability',
            'Data drives continuous AI improvement'
          ]
        }
      ]
    },
    {
      id: 'governance',
      title: 'AI Governance & Risk',
      color: '#8b5cf6',
      desc: 'Responsible AI, compliance, security, and risk management practices.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Ad-hoc Controls',
          bullets: [
            'No formal AI policies',
            'Reactive risk handling',
            'Manual approvals',
            'Limited compliance awareness'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Defined Guidelines',
          bullets: [
            'Basic Responsible AI principles',
            'Defined access controls',
            'Manual risk reviews',
            'Initial compliance mapping'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Operational Governance',
          bullets: [
            'Formal governance bodies',
            'Model risk assessments',
            'Audit trails',
            'Policy enforcement'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'Continuous Assurance',
          bullets: [
            'Automated risk monitoring',
            'Embedded governance',
            'Explainability by design',
            'Regulatory alignment'
          ]
        }
      ]
    },
    {
      id: 'workflows',
      title: 'Workflow Digitization',
      color: '#f97316',
      desc: 'Digitization and automation of core business workflows.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Manual Processes',
          bullets: [
            'Paper or spreadsheet driven',
            'High manual effort',
            'Limited system integration',
            'Low visibility'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Digitized',
          bullets: [
            'Core workflows digitized',
            'Limited automation',
            'System silos remain',
            'Manual handoffs'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Automated',
          bullets: [
            'Workflow automation in place',
            'Cross-system integration',
            'Process metrics tracked',
            'Reduced manual effort'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'Intelligent Workflows',
          bullets: [
            'AI-driven automation',
            'Self-optimizing processes',
            'Real-time insights',
            'Exception-based human oversight'
          ]
        }
      ]
    },
    {
      id: 'infrastructure',
      title: 'Technical Infrastructure',
      color: '#22c55e',
      desc: 'Cloud, platforms, and tooling to support AI workloads.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Basic IT',
          bullets: [
            'Legacy or on-prem heavy',
            'Limited cloud adoption',
            'No AI platforms',
            'Manual provisioning'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Hybrid Cloud',
          bullets: [
            'Cloud-first initiatives',
            'Shared platforms',
            'Limited ML tooling',
            'Manual scaling'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'AI-Capable',
          bullets: [
            'Standard AI platforms',
            'APIs and pipelines',
            'Secure environments',
            'Predictable costs'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'AI Platform at Scale',
          bullets: [
            'MLOps/LLMOps',
            'Automated provisioning',
            'Cost optimization',
            'Enterprise-grade reliability'
          ]
        }
      ]
    },
    {
      id: 'talent',
      title: 'Talent & Skills',
      color: '#14b8a6',
      desc: 'AI literacy, skills, and organizational capabilities.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Limited Skills',
          bullets: [
            'Low AI awareness',
            'No dedicated roles',
            'Ad-hoc learning',
            'Dependency on vendors'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Early Skills',
          bullets: [
            'Small AI team',
            'Basic training',
            'Centralized expertise',
            'Limited business fluency'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Embedded Skills',
          bullets: [
            'Cross-functional teams',
            'Regular upskilling',
            'Business-aligned skills',
            'Internal champions'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'AI-Native Workforce',
          bullets: [
            'AI literacy at scale',
            'Continuous learning',
            'Clear career paths',
            'Innovation culture'
          ]
        }
      ]
    },
    {
      id: 'strategy',
      title: 'Business Alignment & Strategy',
      color: '#eab308',
      desc: 'Alignment of AI initiatives with business goals and strategy.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Unclear Value',
          bullets: [
            'No AI strategy',
            'Disconnected initiatives',
            'Unclear ownership',
            'No KPIs'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Defined Use Cases',
          bullets: [
            'Identified opportunities',
            'Some executive sponsorship',
            'Project-level KPIs',
            'Limited funding'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Strategic Alignment',
          bullets: [
            'Enterprise roadmap',
            'Clear value tracking',
            'Strong sponsorship',
            'Prioritized portfolio'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'Value-Driven AI',
          bullets: [
            'AI embedded in strategy',
            'Outcome-driven funding',
            'Continuous prioritization',
            'Measurable business impact'
          ]
        }
      ]
    },
    {
      id: 'change',
      title: 'Change Management',
      color: '#ef4444',
      desc: 'Culture, adoption, and change enablement.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Resistance',
          bullets: [
            'Low adoption',
            'Change fatigue',
            'Limited communication',
            'Siloed initiatives'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Managed Change',
          bullets: [
            'Communication plans',
            'Training during rollout',
            'Some champions',
            'Reactive support'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Adoption at Scale',
          bullets: [
            'Structured enablement',
            'Feedback loops',
            'High adoption',
            'Cross-team collaboration'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'Continuous Transformation',
          bullets: [
            'Culture of experimentation',
            'Rapid adoption',
            'Employee-driven innovation',
            'Sustained change'
          ]
        }
      ]
    },
    {
      id: 'roi',
      title: 'ROI & Investment Readiness',
      color: '#6366f1',
      desc: 'Ability to fund, measure, and realize value from AI.',
      levels: [
        {
          title: 'Level 1 — Foundational',
          subtitle: 'Unmeasured',
          bullets: [
            'No ROI tracking',
            'Pilot-only funding',
            'Cost seen as risk',
            'No value metrics'
          ]
        },
        {
          title: 'Level 2 — Emerging',
          subtitle: 'Basic ROI',
          bullets: [
            'Cost-benefit analysis',
            'Pilot ROI tracking',
            'Limited scale',
            'Budget constraints'
          ]
        },
        {
          title: 'Level 3 — Scaling',
          subtitle: 'Value Tracking',
          bullets: [
            'Formal ROI models',
            'Portfolio view',
            'Scaling successful pilots',
            'Predictable investment'
          ]
        },
        {
          title: 'Level 4 — Optimized',
          subtitle: 'Value Optimization',
          bullets: [
            'Outcome-based funding',
            'Continuous optimization',
            'AI seen as growth driver',
            'Sustained ROI'
          ]
        }
      ]
    }
  ];

  // Initialize state from user data if available, otherwise use defaults
  const initialUserData = getUserData();
  const [industry, setIndustry] = useState(initialUserData?.industry || 'Retail');
  const [function_, setFunction_] = useState(initialUserData?.primary_function || 'Organization-wide');
  const [size, setSize] = useState(initialUserData?.company_size || '< 500');
  const [evidence, setEvidence] = useState('');

  const currentDimension = dimensions.find(d => d.id === activeDim);

  const selectDimension = (id) => {
    if (id === activeDim) return;
    // Persist current dimension's Evidence & Context before switching (useEffect will load new dimension's notes)
    setState((prev) => {
      const next = { ...prev };
      if (prev[activeDim] && evidence !== undefined) {
        next[activeDim] = { ...prev[activeDim], notes: evidence };
      }
      return next;
    });
    setActiveDim(id);
  };

  const goToPreviousDimension = () => {
    const currentIndex = dimensions.findIndex(d => d.id === activeDim);
    if (currentIndex > 0) {
      const prevDim = dimensions[currentIndex - 1];
      selectDimension(prevDim.id);
    }
  };

  const goToNextDimension = () => {
    const currentIndex = dimensions.findIndex(d => d.id === activeDim);
    if (currentIndex < dimensions.length - 1) {
      const nextDim = dimensions[currentIndex + 1];
      selectDimension(nextDim.id);
    }
  };

  const selectLevel = (levelIndex) => {
    const newState = {
      ...state,
      [activeDim]: {
        ...state[activeDim],
        level: levelIndex
      }
    };
    setState(newState);
  };

  const setStructuredInput = (field, value) => {
    const newState = {
      ...state,
      [activeDim]: {
        ...state[activeDim],
        [field]: value
      }
    };
    setState(newState);
  };

  const handleSave = () => {
    // Persist Evidence & Context (and structured inputs) even when no level is selected
    const newState = {
      ...state,
      [activeDim]: {
        ...state[activeDim],
        notes: evidence
      }
    };
    setState(newState);
  };

  const handleSaveAndNext = () => {
    // Save current dimension data
    handleSave();

    // Check if this is the last dimension
    const currentIndex = dimensions.findIndex(d => d.id === activeDim);
    const isLastDimension = currentIndex === dimensions.length - 1;

    if (isLastDimension) {
      // If last dimension, submit and generate report
      handleSubmitAndGenerate();
    } else {
      // Otherwise, move to next dimension
      const nextDim = dimensions[currentIndex + 1];
      selectDimension(nextDim.id);
    }
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportHtml, setReportHtml] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const handleSubmitAndGenerate = async () => {
    // Validate all dimensions have selections
    const allSelected = dimensions.every(dim => 
      state[dim.id] && state[dim.id].level !== undefined
    );

    if (!allSelected) {
      return;
    }

    setIsGenerating(true);

    try {
      // Prepare selections, notes, and structured inputs (per dimension)
      const selections = {};
      const notes = {};
      const standardization = {};
      const measurability = {};
      
      dimensions.forEach(dim => {
        const dimState = state[dim.id] || {};
        selections[dim.id] = dimState.level;
        if (dimState.notes) {
          notes[dim.id] = dimState.notes;
        }
        if (dimState.standardization) {
          standardization[dim.id] = dimState.standardization;
        }
        if (dimState.measurability) {
          measurability[dim.id] = dimState.measurability;
        }
      });

      // Get user data for context
      const currentUserData = getUserData();
      const userEmail = getUserEmail();

      if (!userEmail) {
        setIsGenerating(false);
        return;
      }

      // Validate context values before sending
      if (!industry || !function_ || !size) {
        console.error('Missing required context values:', { industry, function_, size });
        setIsGenerating(false);
        return;
      }

      // Get organization: login/session user, more-details panel, or stored user (support common keys)
      const organization =
        (currentUserData?.organization ?? currentUserData?.user_organization) ||
        (user?.organization ?? user?.user_organization) ||
        (moreDetailsData?.organization && moreDetailsData.organization.trim()) ||
        '';

      // Prepare context with validated values
      const context = {
        industry: industry.trim(),
        function: function_.trim(),
        size: size.trim(),
        organization: organization.trim() || null // Use null instead of empty string or default
      };

      // Call API to generate report
      const getApiBaseUrl = () => {
        if (process.env.REACT_APP_API_BASE_URL) {
          return process.env.REACT_APP_API_BASE_URL;
        }
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;
        const port = hostname === 'localhost' ? '4000' : (window.location.port || '');
        return `${protocol}//${hostname}${port ? `:${port}` : ''}/api`;
      };
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/questions/users/${encodeURIComponent(userEmail)}/assess-ai-readiness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selections,
          notes,
          standardization,
          measurability,
          context
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      // Handle streaming response (matching GenAI Maturity pattern)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let htmlContent = '';
      let currentSessionId = null;
      let opened = false;
      let openTimer = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              if (data.type === 'html' && data.chunk) {
                htmlContent += data.chunk;
                
                // Open report modal after a delay once streaming starts (matching GenAI pattern)
                if (!opened && !openTimer) {
                  openTimer = setTimeout(() => {
                    opened = true;
                    setShowReport(true);
                    setReportHtml(htmlContent);
                  }, 2000); // 2s delay after first stream chunk
                } else if (opened) {
                  // Update HTML content as it streams in
                  setReportHtml(htmlContent);
                }
              } else if (data.sessionId) {
                currentSessionId = data.sessionId;
                setSessionId(data.sessionId);
              } else if (data.type === 'complete') {
                // Report generation complete
                if (openTimer) {
                  clearTimeout(openTimer);
                }
                setReportHtml(htmlContent);
                setShowReport(true);
                setIsGenerating(false);
                return;
              } else if (data.type === 'error') {
                if (openTimer) {
                  clearTimeout(openTimer);
                }
                throw new Error(data.error || 'Error generating report');
              } else if (data.type === 'progress') {
                // Update progress if needed
                console.log('[AI Readiness] Progress:', data.progress, data.message);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // If we get here, ensure report is shown
      if (openTimer) {
        clearTimeout(openTimer);
      }
      if (htmlContent) {
        setReportHtml(htmlContent);
        setShowReport(true);
      } else {
        throw new Error('No report content received');
      }
      setIsGenerating(false);

    } catch (error) {
      console.error('Error generating report:', error);
      setIsGenerating(false);
    }
  };

  const handleAskNeo = () => {
    setShowNeoChat(true);
  };

  const updateProgress = () => {
    const answered = Object.keys(state).filter(k => state[k] && state[k].level !== undefined).length;
    const percent = Math.round((answered / dimensions.length) * 100);
    return { answered, percent };
  };

  const progress = updateProgress();
  const levelNames = ['Foundational', 'Emerging', 'Scaling', 'Optimized'];
  const levelShort = ['L1', 'L2', 'L3', 'L4'];
  const selectedLevel = state[activeDim] && state[activeDim].level !== undefined
    ? levelNames[state[activeDim].level]
    : '—';

  useEffect(() => {
    const dimState = state[activeDim] || {};
    setEvidence(dimState.notes || '');
  }, [activeDim, state]);

  // Handle click outside to close the more details panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreDetailsPanel) {
        // Check if click is outside both the panel and the button
        const isClickInsidePanel = moreDetailsPanelRef.current && moreDetailsPanelRef.current.contains(event.target);
        const isClickOnButton = moreDetailsButtonRef.current && moreDetailsButtonRef.current.contains(event.target);
        
        if (!isClickInsidePanel && !isClickOnButton) {
          setShowMoreDetailsPanel(false);
        }
      }
    };

    if (showMoreDetailsPanel) {
      // Use a small delay to avoid immediate closure when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMoreDetailsPanel]);

  const handleMoreDetailsChange = (e) => {
    const { name, value } = e.target;
    setMoreDetailsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMoreDetailsSubmit = (e) => {
    e.preventDefault();
    // Here you can add logic to save the data if needed
    // For now, just close the panel
    setShowMoreDetailsPanel(false);
  };

  return (
    <div className="ai-readiness-assessment">
      <div className="w-full pt-2 pb-2" style={{ paddingLeft: '0.5cm', paddingRight: '0.5cm' }}>
        {/* Header */}
        <header className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">
                Relevance Lab — AI Readiness, Risk & ROI Assessment
              </h1>
              <p className="text-sm text-slate-600">
                End-to-end enterprise AI readiness assessment with industry context, structured inputs, and GenAI-powered insights.
              </p>
            </div>
          </div>

          {/* Context Selector */}
          <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Industry</label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
              >
                <option>Retail</option>
                <option>Banking</option>
                <option>Insurance</option>
                <option>Healthcare</option>
                <option>Pharma / Life Sciences</option>
                <option>Manufacturing</option>
                <option>Education</option>
                <option>Technology / SaaS</option>
                <option>Public Sector</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Primary Function</label>
              <select
                value={function_}
                onChange={(e) => setFunction_(e.target.value)}
                className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
              >
                <option>Organization-wide</option>
                <option>IT / Technology</option>
                <option>Finance</option>
                <option>HR</option>
                <option>Operations</option>
                <option>Sales</option>
                <option>Marketing</option>
                <option>Customer Support</option>
              </select>
            </div>
            <div style={{ position: 'relative' }}>
              <div className="flex items-end gap-2">
                <div style={{ flex: 1 }}>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Company Size</label>
                  <select
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
                  >
                    <option>&lt; 500</option>
                    <option>500–2,000</option>
                    <option>2,000–10,000</option>
                    <option>10,000+</option>
                  </select>
                </div>
                <button
                  ref={moreDetailsButtonRef}
                  type="button"
                  onClick={() => setShowMoreDetailsPanel(!showMoreDetailsPanel)}
                  className="add-more-details-btn"
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    height: '2.5rem',
                    marginBottom: '0',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                  }}
                >
                  Add More Details
                </button>
              </div>
              {showMoreDetailsPanel && (
                <div
                  ref={moreDetailsPanelRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    marginTop: '0.5rem',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    zIndex: 1000,
                    minWidth: '300px'
                  }}
                >
                  <form onSubmit={handleMoreDetailsSubmit}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Company Name</label>
                      <input
                        type="text"
                        name="companyName"
                        value={moreDetailsData.companyName}
                        onChange={handleMoreDetailsChange}
                        className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Domain</label>
                      <input
                        type="text"
                        name="domain"
                        value={moreDetailsData.domain}
                        onChange={handleMoreDetailsChange}
                        className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
                        placeholder="Enter domain"
                      />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Organization</label>
                      <input
                        type="text"
                        name="organization"
                        value={moreDetailsData.organization}
                        onChange={handleMoreDetailsChange}
                        className="w-full border border-slate-200 rounded px-2 py-2 text-sm"
                        placeholder="Enter organization"
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowMoreDetailsPanel(false)}
                        style={{
                          backgroundColor: '#e2e8f0',
                          color: '#475569',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <div className="block text-xs font-medium text-slate-600 mb-1" style={{ height: '1.25rem' }}></div>
              <div className="flex items-center" style={{ minHeight: '1.5rem' }}>
                <div className="text-xs text-slate-500">Used for benchmarks & recommendations</div>
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Nav */}
          <aside className="col-span-3">
            {/* Mobile Dropdown - Hidden on desktop */}
            <div className="card dimensions-dropdown-mobile">
              <label className="block text-xs font-medium text-slate-600 mb-2">Assessment Dimensions</label>
              <select
                value={activeDim}
                onChange={(e) => selectDimension(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm font-medium text-slate-800"
              >
                {dimensions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title} {state[d.id] && state[d.id].level !== undefined ? `(${levelShort[state[d.id].level]})` : ''}
                  </option>
                ))}
              </select>
              <div className="mt-2 text-xs text-slate-500">
                {dimensions.find(d => d.id === activeDim)?.desc}
              </div>
            </div>

            {/* Desktop List - Hidden on mobile */}
            <div className="card dimensions-list-desktop" style={{ maxHeight: '72vh', overflow: 'auto' }}>
              <h3 className="font-semibold mb-3 text-indigo-600">Assessment Dimensions</h3>
              <ul className="space-y-2 text-slate-700">
                {dimensions.map((d) => (
                  <li
                    key={d.id}
                    className={`p-3 rounded-md hover:bg-slate-50 cursor-pointer flex items-start justify-between ${activeDim === d.id ? 'bg-indigo-50 border border-indigo-200' : ''}`}
                    onClick={() => selectDimension(d.id)}
                  >
                    <div className="flex items-start">
                      <span
                        className="dim-dot"
                        style={{ background: d.color }}
                      ></span>
                      <div>
                        <div className="font-medium text-slate-800">{d.title}</div>
                        <div className="text-xs text-slate-500 mt-1">{d.desc}</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      {state[d.id] && state[d.id].level !== undefined
                        ? levelShort[state[d.id].level]
                        : ''}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 text-sm text-slate-500">
                <strong>Tip:</strong> Complete all dimensions for a comprehensive readiness score.
              </div>
            </div>

            <div className="card mt-4">
              <h4 className="font-medium mb-2 text-indigo-600">Overall Progress</h4>
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 rounded-full bg-indigo-500"
                  style={{ width: `${progress.percent}%` }}
                ></div>
              </div>
              <div className="mt-2 text-sm text-slate-600">
                {progress.answered === 0
                  ? 'No answers yet'
                  : `${progress.answered}/${dimensions.length} completed (${progress.percent}%)`}
              </div>
            </div>
          </aside>

          {/* Main Panel */}
          <main className="col-span-9">
            {/* Mobile Navigation Arrows */}
            <div className="mobile-nav-arrows">
              {dimensions.findIndex(d => d.id === activeDim) > 0 && (
                <button
                  onClick={goToPreviousDimension}
                  className="mobile-nav-arrow mobile-nav-arrow-left"
                  aria-label="Previous dimension"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              {dimensions.findIndex(d => d.id === activeDim) < dimensions.length - 1 && (
                <button
                  onClick={goToNextDimension}
                  className="mobile-nav-arrow mobile-nav-arrow-right"
                  aria-label="Next dimension"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
            <div className="card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center">
                    <span
                      className="dim-dot"
                      style={{ background: currentDimension?.color || '#4f46e5' }}
                    ></span>
                    <h2 className="text-2xl font-bold text-slate-800">
                      {currentDimension?.title}
                    </h2>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{currentDimension?.desc}</p>
                </div>
                <div className="text-right text-sm text-slate-600">
                  <div>Selected level:</div>
                  <div className="font-semibold text-indigo-600">{selectedLevel}</div>
                </div>
              </div>

              <hr className="border-slate-100 mb-4" />

              <div className="grid grid-cols-4 gap-4 mb-6">
                {currentDimension?.levels.map((lvl, i) => (
                  <div
                    key={i}
                    className={`level-card rounded cursor-pointer ${
                      state[activeDim] && state[activeDim].level === i ? 'selected' : ''
                    }`}
                    onClick={() => selectLevel(i)}
                  >
                    <div>
                      <div className="font-semibold text-indigo-600 mb-1">{lvl.title}</div>
                      <div className="text-xs text-slate-500 mb-3">{lvl.subtitle}</div>
                      <ul className="text-sm text-slate-700 list-disc ml-5 space-y-1">
                        {lvl.bullets.map((bullet, idx) => (
                          <li key={idx}>{bullet}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              {/* Structured Inputs (per dimension) */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Structured Inputs</h4>
                <div className="space-y-3 text-sm text-slate-700">
                  <label className="block">
                    1. How standardized are practices in this dimension?
                    <select
                      className="w-full mt-1 border border-slate-200 rounded px-2 py-1"
                      value={state[activeDim]?.standardization ?? ''}
                      onChange={(e) => setStructuredInput('standardization', e.target.value)}
                    >
                      <option value="">Select...</option>
                      <option value="Not standardized">Not standardized</option>
                      <option value="Partially standardized">Partially standardized</option>
                      <option value="Mostly standardized">Mostly standardized</option>
                      <option value="Fully standardized">Fully standardized</option>
                    </select>
                  </label>
                  <label className="block">
                    2. How measurable are outcomes today?
                    <select
                      className="w-full mt-1 border border-slate-200 rounded px-2 py-1"
                      value={state[activeDim]?.measurability ?? ''}
                      onChange={(e) => setStructuredInput('measurability', e.target.value)}
                    >
                      <option value="">Select...</option>
                      <option value="Not measured">Not measured</option>
                      <option value="Ad-hoc metrics">Ad-hoc metrics</option>
                      <option value="Defined KPIs">Defined KPIs</option>
                      <option value="Continuously optimized">Continuously optimized</option>
                    </select>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Evidence & Context
                </label>
                <input
                  type="text"
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  className="w-full border border-slate-200 rounded p-3 text-sm bg-white text-slate-800 placeholder-slate-400"
                  placeholder="Describe current state, tools, KPIs, challenges, and constraints..."
                />
              </div>

              <div className="mt-4">
                <div className="text-sm text-slate-500 mb-4">
                  Context and structured inputs power follow-up questions, benchmarks, and ROI insights.
                </div>
                <div className="flex items-center justify-end gap-4">
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {(() => {
                      const isLastDimension = dimensions.findIndex(d => d.id === activeDim) === dimensions.length - 1;
                      const hasLevel = state[activeDim] != null && state[activeDim].level !== undefined;
                      const hasStructuredInput = !!(
                        (state[activeDim]?.standardization || '').trim() ||
                        (state[activeDim]?.measurability || '').trim()
                      );
                      const hasEvidence = !!(evidence && (evidence || '').trim().length > 0);
                      const allDimensionsHaveLevel = dimensions.every(dim => state[dim.id] && state[dim.id].level !== undefined);
                      const hasAtLeastOneInput = hasLevel || hasStructuredInput || hasEvidence;
                      const canSaveAndNext = hasAtLeastOneInput;
                      const canSubmit = isLastDimension && allDimensionsHaveLevel && hasAtLeastOneInput;
                      const buttonDisabled = isGenerating || (isLastDimension ? !canSubmit : !canSaveAndNext);
                      return (
                    <button
                      onClick={handleSaveAndNext}
                      disabled={buttonDisabled}
                      className="btn px-4 py-2 bg-green-600 text-white rounded font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      {isGenerating
                        ? 'Generating...'
                        : dimensions.findIndex(d => d.id === activeDim) === dimensions.length - 1
                          ? 'Submit & Get My Report'
                          : 'Save & Next →'
                      }
                    </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* NEO Chatbot */}
      {showNeoChat && (
        <>
          {/* Mobile overlay backdrop */}
          <div
            className="neo-mobile-overlay"
            onClick={() => setShowNeoChat(false)}
          />
          <NeoChatbot
            activeDimension={activeDim}
            dimensions={dimensions}
            sessionId={null}
            showToggleButton={false}
            isOpen={showNeoChat}
            onOpenChange={setShowNeoChat}
          />
        </>
      )}

      {/* Report Display Modal */}
      {showReport && reportHtml && (
        <AIReadinessReport
          htmlContent={reportHtml}
          sessionId={sessionId}
          userEmail={getUserEmail()}
          onClose={() => {
            setShowReport(false);
            setReportHtml(null);
          }}
        />
      )}
    </div>
  );
};

// AI Readiness Report Component
const AIReadinessReport = ({ htmlContent, sessionId, userEmail: propUserEmail, onClose }) => {
  // Get user email from props or localStorage
  const getUserEmail = () => {
    let email = null;
    if (propUserEmail) {
      email = propUserEmail;
    } else {
      const storedUser = localStorage.getItem('aiReadinessUser');
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed.email) email = parsed.email;
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }
    }

    // Normalize email (lowercase and trim) to match backend expectations
    if (email) {
      return email.toLowerCase().trim();
    }

    return null; // Return null instead of fallback to trigger proper error handling
  };

  const userEmail = getUserEmail();
  const [downloading, setDownloading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailToasts, setEmailToasts] = useState([]);
  const emailToastTimersRef = useRef({});
  const emailToastCloseTimersRef = useRef({});

  const dismissEmailToast = (id) => {
    if (emailToastTimersRef.current[id] != null) {
      clearTimeout(emailToastTimersRef.current[id]);
      delete emailToastTimersRef.current[id];
    }
    setEmailToasts((prev) => prev.map((t) => (t.id === id ? { ...t, hiding: true } : t)));
    emailToastCloseTimersRef.current[id] = setTimeout(() => {
      setEmailToasts((prev) => prev.filter((t) => t.id !== id));
      delete emailToastCloseTimersRef.current[id];
    }, 300);
  };

  const addEmailToast = () => {
    const id = Date.now();
    const newToast = { id, hiding: false };
    setEmailToasts((prev) => [...prev, newToast]);
    emailToastTimersRef.current[id] = setTimeout(() => dismissEmailToast(id), 5000);
  };

  useEffect(() => {
    return () => {
      Object.values(emailToastTimersRef.current).forEach(clearTimeout);
      Object.values(emailToastCloseTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const handleDownloadPDF = async () => {
    if (!sessionId || !userEmail) {
      return;
    }

    setDownloading(true);
    try {
      await apiService.generateAndDownloadPDFReport(
        userEmail,
        sessionId,
        (progressMessage) => {
          // Progress callback - can be used for UI updates if needed
        }
      );
    } catch (err) {
      // Error handled silently
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!sessionId || !userEmail) {
      return;
    }

    setSendingEmail(true);
    try {
      await apiService.sendReportEmail(userEmail, sessionId);
      addEmailToast();
    } catch (err) {
      console.error('Error sending email:', err);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" style={{ overflow: 'hidden' }}>
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] flex flex-col" style={{ display: 'flex', flexDirection: 'column', height: '95vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h2 className="text-2xl font-bold text-slate-800">AI Readiness, Risk & ROI Assessment Report</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDownloadPDF}
              disabled={downloading || sendingEmail || !sessionId || !userEmail}
              className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
            >
              {downloading ? 'Downloading...' : 'Download PDF'}
            </button>
            <button
              onClick={handleSendEmail}
              disabled={sendingEmail || !sessionId || !userEmail}
              className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
            >
              {sendingEmail ? 'Sending...' : 'Email Report'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Report Content - Scrollable */}
        <div 
          className="flex-1 p-6" 
          style={{ 
            overflowY: 'auto', 
            overflowX: 'hidden',
            minHeight: 0,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      </div>
    </div>

    {emailToasts.length > 0 && (
      <div className="email-sent-toast-stack" role="status" aria-live="polite">
        {[...emailToasts].reverse().map((toast) => (
          <div
            key={toast.id}
            className={`email-sent-toast ${toast.hiding ? 'hiding' : ''}`}
          >
            <div className="email-sent-toast-content">
              <span className="email-sent-toast-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </span>
              <span className="email-sent-toast-text">Email sent successfully</span>
              <button
                type="button"
                className="email-sent-toast-close"
                onClick={() => dismissEmailToast(toast.id)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
    </>
  );
};

export default AIReadinessAssessment;

